<template>
  <div class="flex max-w-3xl flex-col gap-6">
    <ul class="flex flex-col rounded-lg border" data-test-id="setup-recap-list">
      <li
        v-for="(row, index) in rowsRef"
        :key="row.step"
        class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4"
        :class="{ 'border-t': index > 0 }"
        :data-test-id="`setup-recap-${row.step}`"
      >
        <span
          class="size-2.5 shrink-0 rounded-full"
          :class="row.complete ? 'bg-success' : 'bg-border'"
          aria-hidden="true"
        />
        <div class="flex min-w-0 flex-[1_1_15rem] flex-col gap-0.5">
          <span class="text-sm font-semibold">{{ t(`admin.setup.steps.${row.step}.label`) }}</span>
          <ul class="flex flex-wrap gap-x-4 gap-y-1">
            <li
              v-for="item in row.items"
              :key="item.key"
              class="flex items-center gap-1.5 text-sm leading-snug text-muted-foreground"
            >
              <CheckIcon v-if="item.done" class="size-3.5 text-success" aria-hidden="true" />
              <CircleIcon v-else class="size-3.5" aria-hidden="true" />
              {{ t(`admin.setup.checklist.items.${item.key}`) }}
              <span class="sr-only">
                ({{ item.done ? t('admin.setup.recap.status.complete') : t('admin.setup.recap.status.todo') }})
              </span>
            </li>
          </ul>
        </div>
        <Badge
          :variant="row.complete ? 'default' : 'secondary'"
          :class="row.complete ? 'bg-success text-success-foreground' : ''"
        >
          {{ row.complete ? t('admin.setup.recap.status.complete') : t('admin.setup.recap.status.todo') }}
        </Badge>
        <Button variant="link" size="sm" class="px-0" @click="emit('edit', row.step)">
          {{ t('admin.setup.recap.edit') }}
          <span class="sr-only">{{ t(`admin.setup.steps.${row.step}.label`) }}</span>
        </Button>
      </li>
    </ul>

    <section class="flex flex-col gap-1 rounded-lg bg-accent p-5">
      <h2 class="text-base font-semibold text-accent-foreground">{{ t('admin.setup.recap.next.title') }}</h2>
      <p class="text-sm leading-normal">{{ t('admin.setup.recap.next.description') }}</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { CheckIcon, CircleIcon } from 'lucide-vue-next'
import { computed } from 'vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { SetupStepType } from '@/features/admin/platform-setup/constants'
import { SETUP_STEPS } from '@/features/admin/platform-setup/constants'
import { usePlatformSetup } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import { useI18n } from '@/lib/i18n'

const emit = defineEmits<{
  edit: [step: SetupStepType]
}>()

const { t } = useI18n()
const { checklistRef } = usePlatformSetup()

const rowsRef = computed(() =>
  // L'équipe est facultative : elle n'a pas de point dans la checklist.
  SETUP_STEPS.filter((step) => step !== 'recap' && step !== 'team').map((step) => {
    const items = checklistRef.value.filter((item) => item.step === step)
    return {
      step,
      complete: items.every((item) => item.done),
      items,
    }
  }),
)
</script>
